import { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { MessageOptions, errorToast, warningToast } from '../components/Toasty';
import { DataSourceCell, ExcelDataSource } from '../types/Data/DataSource';

import ExcelJS from 'exceljs';

interface DataSourceDropzoneProps {
    onFileDrop: (excelDataSource: ExcelDataSource) => void,
};

const DataSourceDropzone = ({ onFileDrop }: DataSourceDropzoneProps) => {
  const onDrop = useCallback((acceptedFiles: File[]) => {
    acceptedFiles.forEach((file: File) => {
      const reader = new FileReader()

      reader.onabort = () => {
        const msg = (
          <div>
            File reading was aborted!
          </div>
        );
        const autoClose = true;
        warningToast(msg, autoClose);
      }
      reader.onerror = () => {
        const msg = (
          <div>
            File reading has failed!
          </div>
        );
        const options: MessageOptions = {
          autoClose: 3999,
          pauseOnHover: true,
          customID: undefined,
        }
        errorToast(msg, options);
      }
      console.log('')
      reader.onload = async (e: ProgressEvent<FileReader>) => {
        const data = e.target?.result as ArrayBuffer;
        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(data);

        const worksheet = workbook.worksheets[0];

        // Extract headers from the first row
        const headerStrings: string[] = [];
        const firstRow = worksheet.getRow(1);
        firstRow.eachCell({ includeEmpty: false }, (cell) => {
          headerStrings.push(cell.value?.toString() ?? '');
        });

        // Contains the cells that will be added back to the DS for saving
        const cells: string[][] = [];
        // Loop through rows starting from row 2 (skip header)
        worksheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
          if (rowNumber === 1) return; // Skip header row
          const rowData: string[] = [];
          headerStrings.forEach((_, colIndex) => {
            const cellValue = row.getCell(colIndex + 1).value;
            rowData.push(cellValue?.toString() ?? '');
          });
          cells.push(rowData);
        });

        const excelDataSource: ExcelDataSource = {
          headers: headerStrings.map((value) => ({
            name: value,
            displayAt: 'None',
          })),
          rows: cells.map((row, r) => (
              row.map((data, c) => {
                const validations: string[] = [];
                let x: DataSourceCell;
                x = {
                  data,
                  rowIndex: r,
                  columnIndex: c,
                  modified: false,
                  editable: false,
                  visible: false,
                  selected: false,
                  validations,
                  error: false,
                }
                return x;
              })
          )),
        };

        onFileDrop(excelDataSource);
      };

      reader.readAsArrayBuffer(file);
    })
    
  }, [])

  /**
   * Accepted file MIME types can be found here: https://developer.mozilla.org/en-US/docs/Web/HTTP/Basics_of_HTTP/MIME_types/Common_types
   */
  const {getRootProps, getInputProps} = useDropzone({
    onDrop,
    maxFiles: 1,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    }
  })

  return (
    <div {...getRootProps()}>
      <input {...getInputProps()} />
      <p>Drag 'n' drop an Excel file, or click to select a file</p>
    </div>
  )
}

export default DataSourceDropzone;